import type * as Koala from '@getkoala/browser';
import React from 'react';

import { useSafeLayoutEffect } from '#/hooks/use-safe-layout-effect';

import { getKoala } from './koala';

export interface KoalaContextType {
  ready: boolean;
  koala: Koala.KoalaSDK | null;
}

export const KoalaContext = React.createContext<KoalaContextType>({
  ready: false,
  koala: getKoala(),
});

export type KoalaProviderProps = React.PropsWithChildren;

export function KoalaProvider({ children }: KoalaProviderProps) {
  const koala = useLazyLoadKoala();
  const [ready, setReady] = React.useState<boolean>(false);

  useSafeLayoutEffect(() => {
    if (!koala) return;

    let canceled = false;

    koala.ready(() => {
      if (!canceled) {
        setReady(true);
      }
    });

    return () => {
      canceled = true;
    };
  }, [koala]);

  const state = React.useMemo(() => ({ ready, koala }), [ready, koala]);
  return <KoalaContext.Provider value={state}>{children}</KoalaContext.Provider>;
}

export function useKoala() {
  return React.useContext(KoalaContext);
}

/*
|------------------
| Internals
|------------------
*/

function useLazyLoadKoala() {
  const [koala, setKoala] = React.useState<Koala.KoalaSDK | null>(getKoala());

  useSafeLayoutEffect(() => {
    if (koala) return;

    const interval = setInterval(() => {
      const koala = getKoala();
      if (koala) {
        setKoala(koala);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [koala]);

  return koala;
}
